import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import LoadingComponent from "../LoadingComponent";
import {
  RiMedalLine,
  RiSparkling2Line,
  RiLineChartLine,
} from "react-icons/ri";
import { API_BASE_URL } from "../../config";

const slugify = (s = "") =>
  String(s)
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-");

const ICON_CLASS =
  "text-white/80 shrink-0 w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6";

const ReleasedSubjectsCard = ({
  subject_image,
  subject_title,
  exam_type,
  subjectId,
  variant = "featured",
}) => {
  const navigate = useNavigate();
  const isCompact = variant === "compact";

  const [subjectProgress, setSubjectProgress] = useState(0);
  const [enrollmentStatus, setEnrollmentStatus] = useState("unknown");
  const [progressLoading, setProgressLoading] = useState(false);
  const [enrollLoading, setEnrollLoading] = useState(false);
  const [moduleLoading, setModuleLoading] = useState(false);
  const [entryModuleSlug, setEntryModuleSlug] = useState(null);

  const userInfo = JSON.parse(localStorage.getItem("user") || "{}");
  const studentId = userInfo?.id;
  const token = localStorage.getItem("auth_token");

  const examSlug = slugify(exam_type);
  const subjectSlug = slugify(subject_title);

  const subjectBasePath = `/${examSlug}/${subjectSlug}`;
  const quickStartPath = entryModuleSlug
    ? `${subjectBasePath}/${entryModuleSlug}/learn?random=25&mode=quick`
    : subjectBasePath;
  const registerPath = `/register?next=${encodeURIComponent(quickStartPath)}`;

  const getStatus = (progress) => {
    if (progress === 100) return "Completed";
    if (progress > 0) return "In Progress";
    return "Not Started";
  };

  const displayProgress = Math.min(subjectProgress, 100);
  const isEnrolled = enrollmentStatus === "enrolled";
  const isStillLoading =
    enrollmentStatus === "unknown" ||
    moduleLoading ||
    (isEnrolled && progressLoading);

  const imageHeight = isCompact
    ? "h-[240px] sm:h-[260px] xl:h-[280px]"
    : "h-[170px] sm:h-[210px] lg:h-[240px]";

  const features = useMemo(
    () => [
      {
        icon: <RiMedalLine className={ICON_CLASS} />,
        title: "Quality-Checked",
        subtitle: "Reviewed content",
      },
      {
        icon: <RiSparkling2Line className={ICON_CLASS} />,
        title: "Smart Quiz",
        subtitle: "Targets weak areas",
      },
      {
        icon: <RiLineChartLine className={ICON_CLASS} />,
        title: "Track Progress",
        subtitle: "Mastery %",
      },
    ],
    []
  );

  useEffect(() => {
    if (!subjectId) {
      setEntryModuleSlug(null);
      return;
    }

    const fetchEntryModule = async () => {
      try {
        setModuleLoading(true);

        const response = await axios.get(`${API_BASE_URL}/subject_modules`, {
          params: { subjectId },
        });

        const firstModule = [...(response.data || [])].sort((a, b) => {
          const aNum = a.module_number ?? Number.MAX_SAFE_INTEGER;
          const bNum = b.module_number ?? Number.MAX_SAFE_INTEGER;

          if (aNum !== bNum) return aNum - bNum;
          return String(a.module_name || "").localeCompare(
            String(b.module_name || "")
          );
        })[0];

        setEntryModuleSlug(firstModule ? slugify(firstModule.module_name) : null);
      } catch (error) {
        console.error("Error fetching entry module:", error);
        setEntryModuleSlug(null);
      } finally {
        setModuleLoading(false);
      }
    };

    fetchEntryModule();
  }, [subjectId]);

  useEffect(() => {
    if (!studentId || !subjectId) {
      setEnrollmentStatus("not_enrolled");
      return;
    }

    const fetchEnrollment = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/enrollments`, {
          params: { student_id: studentId, subject_id: subjectId },
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });

        const enrollments = response.data || [];
        setEnrollmentStatus(
          enrollments.length > 0 ? "enrolled" : "not_enrolled"
        );
      } catch (error) {
        console.error("Error checking enrollment:", error);
        setEnrollmentStatus("not_enrolled");
      }
    };

    fetchEnrollment();
  }, [studentId, subjectId, token]);

  useEffect(() => {
    if (enrollmentStatus !== "enrolled") {
      setSubjectProgress(0);
      return;
    }

    if (!studentId || !subjectId) return;

    const fetchSubjectMastery = async () => {
      try {
        setProgressLoading(true);

        const response = await axios.get(
          `${API_BASE_URL}/api/subject_mastery_statistics`,
          {
            params: { student_id: studentId, subject_id: subjectId },
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          }
        );

        const percentage = response.data?.subject_mastery_percentage ?? 0;
        setSubjectProgress(Math.round(percentage));
      } catch (error) {
        console.error("Error computing subject progress:", error);
        setSubjectProgress(0);
      } finally {
        setProgressLoading(false);
      }
    };

    fetchSubjectMastery();
  }, [enrollmentStatus, studentId, subjectId, token]);

  const handleOpenIfEnrolled = () => {
    if (!isEnrolled) return;

    const hasPracticed = displayProgress > 0;
    navigate(hasPracticed ? subjectBasePath : quickStartPath);
  };

  const handleEnrollAndGo = async () => {
    if (!token || !studentId) {
      navigate(registerPath);
      return;
    }

    if (isEnrolled) {
      navigate(quickStartPath);
      return;
    }

    try {
      setEnrollLoading(true);

      await axios.post(
        `${API_BASE_URL}/api/create/enrollments/${studentId}`,
        { subject_id: subjectId, progress: 0 },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setEnrollmentStatus("enrolled");
      navigate(quickStartPath);
    } catch (error) {
      console.error("Error enrolling student:", error);
    } finally {
      setEnrollLoading(false);
    }
  };

  const handlePrimaryAction = () => {
    if (isEnrolled) {
      handleOpenIfEnrolled();
    } else {
      handleEnrollAndGo();
    }
  };

  if (isStillLoading) {
    return (
      <div
        className={`w-full rounded-2xl bg-[#022935] border border-white/10 shadow-lg ${
          isCompact ? "min-h-[240px]" : "p-4 sm:p-5"
        } flex items-center justify-center`}
      >
        <LoadingComponent />
      </div>
    );
  }

  if (isCompact) {
    return (
      <button
        type="button"
        onClick={enrollLoading ? undefined : handlePrimaryAction}
        disabled={enrollLoading}
        className="group relative w-full overflow-hidden rounded-2xl border border-white/10 bg-[#022935] shadow-lg text-left"
      >
        <div className={`relative ${imageHeight}`}>
          <img
            src={subject_image}
            alt={`${subject_title} cover`}
            className="w-full h-full object-cover transition duration-500 group-hover:scale-[1.03]"
          />

          <div className="absolute inset-0 bg-gradient-to-t from-[#04181f]/95 via-[#04181f]/55 to-black/10" />

          <div className="absolute top-3 left-3 right-3 flex items-start justify-between gap-2">
            <span className="rounded-full border border-white/15 bg-black/30 px-2.5 py-1 text-[10px] sm:text-xs font-medium tracking-wide text-white/90 backdrop-blur-sm">
              {exam_type}
            </span>

            <span
              className={`rounded-full px-2.5 py-1 text-[10px] sm:text-xs font-medium backdrop-blur-sm border ${
                isEnrolled
                  ? "border-emerald-300/30 bg-emerald-400/15 text-emerald-100"
                  : "border-sky-300/30 bg-sky-400/15 text-sky-100"
              }`}
            >
              {isEnrolled
                ? `${getStatus(displayProgress)} • ${displayProgress}%`
                : enrollLoading
                ? "Enrolling..."
                : "Free Beta"}
            </span>
          </div>

          <div className="absolute inset-x-0 bottom-0 p-4 sm:p-5">
            <p className="text-lg sm:text-xl font-semibold text-white leading-tight">
              {subject_title}
            </p>

            <p className="mt-1 text-xs sm:text-sm text-white/80">
              {isEnrolled
                ? displayProgress > 0
                  ? "Continue where you left off."
                  : "Start your first guided quiz."
                : "Tap to unlock the first quiz."}
            </p>

            {isEnrolled ? (
              <div className="mt-3">
                <div className="h-2 rounded-full overflow-hidden bg-white/35 border border-white/10">
                  <div
                    className={`h-full rounded-full ${
                      progressLoading
                        ? "bg-emerald-300 animate-pulse"
                        : "bg-emerald-400"
                    }`}
                    style={{ width: `${displayProgress}%` }}
                  />
                </div>

                <div className="mt-3 inline-flex items-center rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-medium text-white/90 backdrop-blur-sm">
                  {displayProgress > 0 ? "Continue Studying" : "Start First Quiz"}
                </div>
              </div>
            ) : (
              <div className="mt-3 inline-flex items-center rounded-full border border-sky-300/25 bg-sky-400/15 px-3 py-1.5 text-xs font-medium text-sky-50 backdrop-blur-sm">
                Start First Quiz
              </div>
            )}
          </div>
        </div>
      </button>
    );
  }

  return (
    <div className="relative overflow-hidden rounded-[28px] border border-white/10 bg-[#022935] shadow-[0_20px_60px_rgba(0,0,0,0.35)]">
      <div className="grid grid-cols-1 lg:grid-cols-[1.08fr_0.92fr] min-h-[300px] lg:min-h-[320px]">
        <div className="relative min-h-[190px] sm:min-h-[220px] lg:min-h-[320px]">
          <img
            src={subject_image}
            alt={`${subject_title} cover`}
            className="absolute inset-0 h-full w-full object-cover"
          />

          <div className="absolute inset-0 bg-gradient-to-r from-[#03131a]/25 via-[#03131a]/20 to-[#03131a]/80 lg:bg-gradient-to-r lg:from-[#03131a]/10 lg:via-[#03131a]/20 lg:to-[#03131a]/85" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#03131a]/90 via-transparent to-transparent" />

          <div className="absolute left-4 right-4 top-4 flex items-start justify-between gap-3">
            <span className="rounded-full border border-white/15 bg-black/30 px-3 py-1 text-[11px] sm:text-xs font-medium tracking-[0.12em] text-white/90 backdrop-blur-sm">
              {exam_type}
            </span>

            <span
              className={`rounded-full px-3 py-1 text-[11px] sm:text-xs font-medium backdrop-blur-sm border ${
                isEnrolled
                  ? "border-emerald-300/30 bg-emerald-400/15 text-emerald-100"
                  : "border-sky-300/30 bg-sky-400/15 text-sky-100"
              }`}
            >
              {isEnrolled
                ? `${getStatus(displayProgress)} • ${displayProgress}%`
                : enrollLoading
                ? "Enrolling..."
                : "Free Beta"}
            </span>
          </div>

          <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5 lg:p-5">
            <p className="max-w-xl text-lg sm:text-xl lg:text-2xl font-semibold text-white leading-tight">
              {subject_title}
            </p>

            <p className="mt-1.5 max-w-lg text-xs sm:text-sm text-white/80 leading-relaxed">
              {isEnrolled
                ? displayProgress > 0
                  ? "Pick up where you left off and keep building momentum."
                  : "You’re enrolled — start strong with your first guided quiz."
                : "Start with a focused first quiz and build mastery from the beginning."}
            </p>
          </div>
        </div>

        <div className="relative flex flex-col justify-between bg-[linear-gradient(180deg,rgba(5,32,42,0.95),rgba(3,24,32,0.98))] p-4 sm:p-5 lg:p-5">
          <div>
            <div className="mt-1">
              {isEnrolled ? (
                <>
                  <p className="text-sm font-medium text-emerald-300">
                    {getStatus(displayProgress)} ({displayProgress}%)
                  </p>

                  <div className="mt-3 h-3 rounded-full overflow-hidden bg-white/35 border border-white/10">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${
                        progressLoading
                          ? "bg-emerald-300 animate-pulse"
                          : "bg-emerald-400"
                      }`}
                      style={{ width: `${displayProgress}%` }}
                    />
                  </div>

                  <p className="mt-3 text-sm text-white/70 leading-relaxed">
                    {displayProgress > 0
                      ? "You already have momentum here. Continue your subject journey or jump back into practice."
                      : "You’re enrolled and ready to begin. Start with your first quiz and unlock your flow."}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm font-medium text-sky-200">Free Beta Access</p>
                  <p className="mt-3 text-sm text-white/70 leading-relaxed">
                    No payment required right now. Enroll and go straight into the first quiz.
                  </p>
                </>
              )}
            </div>

            <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-2.5">
              {features.map((feature) => (
                <div
                  key={feature.title}
                  className="rounded-2xl border border-white/10 bg-white/5 px-3 py-2.5"
                >
                  <div className="flex items-center gap-2">
                    {feature.icon}
                    <p className="text-[11px] sm:text-xs font-semibold text-white leading-tight">
                      {feature.title}
                    </p>
                  </div>
                  <p className="mt-1.5 text-[10px] sm:text-[11px] text-white/60 leading-relaxed">
                    {feature.subtitle}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {isEnrolled ? (
              <>
                <button
                  type="button"
                  onClick={handleOpenIfEnrolled}
                  className="inline-flex cols-1 items-center justify-center rounded-2xl bg-white px-5 py-3 text-sm font-semibold text-[#032734] hover:bg-white/90 transition"
                >
                  {displayProgress > 0 ? "Continue Studying" : "Start First Quiz"}
                </button>

                <button
                  type="button"
                  onClick={() => navigate(subjectBasePath)}
                  className="inline-flex items-center cols-1 justify-center rounded-2xl border border-white/15 bg-white/5 px-5 py-3 text-sm font-medium text-white hover:bg-white/10 transition"
                >
                  Subject Overview
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={enrollLoading ? undefined : handleEnrollAndGo}
                disabled={enrollLoading}
                className={`inline-flex items-center justify-center rounded-2xl px-5 py-3 text-sm font-semibold text-white transition ${
                  enrollLoading
                    ? "bg-sky-900/60 cursor-not-allowed"
                    : "bg-[#0b6b8a] hover:bg-sky-600"
                }`}
              >
                {enrollLoading ? "Enrolling..." : "Start First Quiz"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReleasedSubjectsCard;